import { useOptimizationGroupDataUpdate, useSnackbar, useTokenInfo } from '@e2m-customer-portal/hooks';
import { mustBeGreaterThan, Nullable, UserRole, validation, Validation } from '@e2m-customer-portal/shared';
import {
  DayOfWeek,
  Drawer,
  DrawerButtons,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  InfoTooltip,
  NumberInput,
  TextInput,
  TimePicker,
  ValidatedContainer,
  WeekdaySelection,
  WeekdaySelector,
} from '@e2m-customer-portal/ui';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import clsx from 'clsx';
import { format, getHours, getMinutes, roundToNearestMinutes, setHours, setMinutes } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  AvailabilityMetricMethod,
  OptimizationGroupParametersType,
  OptimizationParameterValue,
} from 'domains/optimization-group/types';
import { equals, range } from 'ramda';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validatedInput } from 'utils';
import { getOptimizationSchedule, OptimizationSchedule } from './utils';

function getWeekdaySelectionValidation(message: string): Validation<WeekdaySelection | undefined> {
  return validation(message, (value) => value != null && range(0, 7).some((day) => value[day as DayOfWeek]));
}

const ValidatedWeekdaySelector = validatedInput(WeekdaySelector, 'selected');

interface OptimizationParametersSidePanelProps {
  data: OptimizationGroupParametersType;
  id: string;
  open: boolean;
  onClose: () => void;
}

function OptimizationParametersSidePanel({ data, id, open, onClose }: Readonly<OptimizationParametersSidePanelProps>) {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const { roles } = useTokenInfo();
  const isVPPOperator = roles.has(UserRole.VPPOperator);
  const [isValid, setIsValid] = useState(false);
  const { showError, showSuccess } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [update] = useOptimizationGroupDataUpdate();

  //temporary readonly for non-biogas assets
  const readOnly = (item?: string) => !isVPPOperator;

  const weekdaySelectionRules = useMemo(
    () => [getWeekdaySelectionValidation(t('FORECAST_ASSISTANT_ERROR_SELECT_WEEKDAYS'))],
    [t],
  );

  if (!data) return <div className="font-medium test-lg p-4">{t('COPT_MISSING_PARAMETERS_MESSAGE')}</div>;

  const [updatedData, setUpdatedData] = useState<OptimizationGroupParametersType>({ ...data });
  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());
  const [updatedBillingDataConfig, setUpdatedBillingDataConfig] = useState(data?.billing_data_config);
  const [optimizationSchedule, setOptimizationSchedule] = useState<OptimizationSchedule | null>(
    getOptimizationSchedule(data.opt_run_schedule ?? null),
  );

  const updateValues = () => {
    setIsSaving(true);
    const updatedItems = Object.fromEntries(changes);
    if (isValid) {
      const mutationBody = JSON.stringify({
        asset_type: data.asset_type,
        ...updatedItems,
      });

      update(id, mutationBody)
        .then((res) => {
          if (res.data?.OptimizationGroupDataUpdate.json) {
            showSuccess(t('COPT_EDIT_SUCCESS_MESSAGE'));
          }
        })
        .catch((error) => {
          showError(error);
        })
        .finally(() => {
          setIsSaving(false);
          setChanges(new Map());
          onClose();
        });
    }
  };

  const resetValues = () => {
    setUpdatedData({ ...data });
    setUpdatedBillingDataConfig(data?.billing_data_config);
    setChanges(new Map());
    setOptimizationSchedule(getOptimizationSchedule(data.opt_run_schedule ?? null));
  };

  useEffect(() => {
    if (open) {
      setUpdatedData({ ...data });
    }
  }, [open]);

  useEffect(() => {
    if (updatedBillingDataConfig && !equals(updatedBillingDataConfig, data?.billing_data_config)) {
      setChanges(changes.set('billing_data_config', updatedBillingDataConfig));
    }
  }, [updatedBillingDataConfig]);

  if (!data) {
    return;
  }

  return (
    <Drawer open={open} onClose={onClose} testId="optimization-parameters-side-panel" disableEnforceFocus>
      <DrawerHeader cancelLabel={t('SHARED_CANCEL')} noDivider></DrawerHeader>
      <DrawerContent className="pt-2 *:pt-4" unknownErrorLabel={t('SHARED_ERROR_UNKNOWN')}>
        <h2 className="-mt-4 mb-0 text-base font-semibold">{t('COPT_GROUP_META')}</h2>
        <div className="pb-2 mb-2 border-0 border-solid border-b border-b-divider">
          <div className="grid grid-cols-2 gap-6 *:pb-2 ">
            {updatedData?.name !== undefined && (
              <div className="*:m-0 *:pt-1">
                <p className="text-sm text-grey-300">{t('COPT_GROUP_NAME')}</p>
                <p className="text-sm font-bold">{updatedData.name}</p>
              </div>
            )}
            {updatedData?.status !== undefined && (
              <div className="*:m-0 *:pt-1">
                <p className="text-sm text-grey-300">{t('COPT_STATUS')}</p>
                <p className="text-sm font-bold">{updatedData.status}</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-6 *:pb-2">
            {updatedData?.valid_from_timestamp !== undefined && (
              <div className="*:m-0 *:pt-1">
                <p className="text-sm text-grey-300">{t('COPT_VALID_FROM_TIMESTAMP')}</p>
                <p className="text-sm font-bold"> {format(updatedData.valid_from_timestamp, 'Pp', { locale: de })}</p>
              </div>
            )}
            {updatedData?.asset_type !== undefined && (
              <div className="*:m-0 *:pt-1">
                <p className="text-sm text-grey-300">{t('COPT_ASSET_TYPE')}</p>
                <p className="text-sm font-bold">{updatedData.asset_type}</p>
              </div>
            )}
          </div>
        </div>
        <ValidatedContainer validate="on" onValidated={setIsValid}>
          {updatedData?.grid_connection_capacity_in !== undefined && (
            <NumberInput
              value={
                updatedData.grid_connection_capacity_in === null ? null : updatedData.grid_connection_capacity_in * 1000
              }
              name="grid_connection_capacity_in"
              label={t('COPT_GRID_CONNECTION_CAPACITY_IN')}
              required
              onChange={(value: number) => {
                const grid_connection_capacity_in = value != null && !Number.isNaN(value) ? value / 1000 : null;
                setUpdatedData({
                  ...updatedData,
                  grid_connection_capacity_in,
                });
                setChanges(changes.set('grid_connection_capacity_in', grid_connection_capacity_in));
              }}
              disabled={readOnly('grid_connection_capacity_in')}
              rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
              endAdornment={<InfoTooltip field="grid_connection_capacity_in" />}
            />
          )}
          {updatedData?.grid_connection_capacity_out !== undefined && (
            <NumberInput
              value={
                updatedData.grid_connection_capacity_out === null
                  ? null
                  : updatedData.grid_connection_capacity_out * 1000
              }
              name="grid_connection_capacity_out"
              label={t('COPT_GRID_CONNECTION_CAPACITY_OUT')}
              required
              onChange={(value: number) => {
                const grid_connection_capacity_out = value != null && !Number.isNaN(value) ? value / 1000 : null;
                setUpdatedData({
                  ...updatedData,
                  grid_connection_capacity_out,
                });
                setChanges(changes.set('grid_connection_capacity_out', grid_connection_capacity_out));
              }}
              disabled={readOnly('grid_connection_capacity_out')}
              rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
              endAdornment={<InfoTooltip field="grid_connection_capacity_out" />}
            />
          )}
          {updatedData.pv_forecast_buffer !== undefined && (
            <NumberInput
              value={updatedData.pv_forecast_buffer}
              name="pv_forecast_buffer"
              label={t('COPT_PV_FORECAST_BUFFER')}
              onChange={(value: Nullable<number>) => {
                const pv_forecast_buffer = value != null && !Number.isNaN(value) ? value : null;
                setUpdatedData({
                  ...updatedData,
                  pv_forecast_buffer,
                });
                setChanges(changes.set('pv_forecast_buffer', pv_forecast_buffer));
              }}
              disabled={readOnly('pv_forecast_buffer')}
              notNegative
              maxValue={1}
              endAdornment={<InfoTooltip field="pv_forecast_buffer" />}
            />
          )}
          {updatedData.power_buffer_coupled_on_pv_on !== undefined && (
            <label className="flex items-center text-sm font-light mr-4 -mt-2">
              <input
                type="checkbox"
                checked={!!updatedData.power_buffer_coupled_on_pv_on}
                disabled={readOnly('power_buffer_coupled_on_pv_on')}
                onChange={() => {
                  const power_buffer_coupled_on_pv_on = !updatedData.power_buffer_coupled_on_pv_on;
                  setUpdatedData({ ...updatedData, power_buffer_coupled_on_pv_on });
                  setChanges(changes.set('power_buffer_coupled_on_pv_on', power_buffer_coupled_on_pv_on));
                }}
                className="w-4 h-4 mr-2 accent-green-200"
              />
              {t('COPT_POWER_BUFFER_COUPLED_ON_PV_ON')}
              <InfoTooltip field="power_buffer_coupled_on_pv_on" />
            </label>
          )}
          {(updatedData.power_buffer_percentage !== undefined || updatedData.power_buffer_fixed !== undefined) && (
            <div className="flex gap-2">
              {updatedData.power_buffer_percentage !== undefined && (
                <NumberInput
                  value={
                    updatedData.power_buffer_percentage === null
                      ? null
                      : Math.round(updatedData.power_buffer_percentage * 100)
                  }
                  name="power_buffer_percentage"
                  label={t('COPT_POWER_BUFFER_PERCENTAGE')}
                  required
                  onChange={(value: Nullable<number>) => {
                    const power_buffer_percentage =
                      value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      power_buffer_percentage,
                    });
                    setChanges(changes.set('power_buffer_percentage', power_buffer_percentage));
                  }}
                  disabled={readOnly('power_buffer_percentage')}
                  notNegative
                  maxValue={100}
                  endAdornment={<InfoTooltip field="power_buffer_percentage" />}
                />
              )}
              {updatedData.power_buffer_fixed !== undefined && (
                <NumberInput
                  value={
                    updatedData.power_buffer_fixed === null ? null : Math.round(updatedData.power_buffer_fixed * 1000)
                  }
                  name="power_buffer_fixed"
                  label={t('COPT_POWER_BUFFER_FIXED')}
                  required
                  onChange={(value: number) => {
                    const power_buffer_fixed = value != null && !Number.isNaN(value) ? Math.round(value) / 1000 : null;
                    setUpdatedData({
                      ...updatedData,
                      power_buffer_fixed,
                    });
                    setChanges(changes.set('power_buffer_fixed', power_buffer_fixed));
                  }}
                  disabled={readOnly('power_buffer_fixed')}
                  notNegative
                  endAdornment={<InfoTooltip field="power_buffer_fixed" />}
                />
              )}
            </div>
          )}
          {(updatedData.ppa_price !== undefined || updatedData.market_premium !== undefined) && (
            <div className="flex gap-2">
              {updatedData.ppa_price !== undefined && (
                <NumberInput
                  value={updatedData.ppa_price}
                  name="ppa_price"
                  label={t('COPT_PPA_PRICE')}
                  title={t('COPT_PPA_PRICE')}
                  onChange={(value: Nullable<number>) => {
                    const ppa_price = value != null && !Number.isNaN(value) ? value : null;
                    setUpdatedData({ ...updatedData, ppa_price });
                    setChanges(changes.set('ppa_price', ppa_price));
                  }}
                  disabled={readOnly('ppa_price')}
                />
              )}
              {updatedData.market_premium !== undefined && (
                <NumberInput
                  value={updatedData.market_premium}
                  name="market_premium"
                  label={t('COPT_MARKET_PREMIUM')}
                  title={t('COPT_MARKET_PREMIUM')}
                  required
                  onChange={(value: number) => {
                    const market_premium = value != null && !Number.isNaN(value) ? value : null;
                    setUpdatedData({
                      ...updatedData,
                      market_premium,
                    });
                    setChanges(changes.set('market_premium', market_premium));
                  }}
                  disabled={readOnly('market_premium')}
                />
              )}
            </div>
          )}
          {(updatedData.robotron_name !== undefined || updatedData.inverter_efficiency !== undefined) && (
            <div className="flex gap-2">
              {updatedData.robotron_name !== undefined && (
                <TextInput
                  value={updatedData.robotron_name}
                  name="robotron_name"
                  label={t('COPT_ROBOTRON_NAME')}
                  title={t('COPT_ROBOTRON_NAME')}
                  disabled
                />
              )}
              {updatedData.inverter_efficiency !== undefined && (
                <NumberInput
                  value={
                    updatedData.inverter_efficiency === null ? null : Math.round(updatedData.inverter_efficiency * 100)
                  }
                  name="inverter_efficiency"
                  label={t('COPT_INVERTER_EFFICIENCY')}
                  title={t('COPT_INVERTER_EFFICIENCY')}
                  required
                  onChange={(value: number) => {
                    const inverter_efficiency = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      inverter_efficiency,
                    });
                    setChanges(changes.set('inverter_efficiency', inverter_efficiency));
                  }}
                  disabled={readOnly('inverter_efficiency')}
                  maxValue={100}
                  rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                />
              )}
            </div>
          )}
          {updatedData.profit_share !== undefined && (
            <label className="flex items-center text-sm font-light mr-4 xl:col-span-2" title={t('COPT_PROFIT_SHARE')}>
              <input
                type="checkbox"
                checked={!!updatedData.profit_share}
                disabled={readOnly('profit_share')}
                onChange={() => {
                  const profit_share = !updatedData.profit_share;
                  setUpdatedData({ ...updatedData, profit_share });
                  setChanges(changes.set('profit_share', profit_share));
                }}
                className="w-4 h-4 mr-2 accent-green-200"
              />
              {t('COPT_PROFIT_SHARE')}
            </label>
          )}
          {updatedBillingDataConfig !== undefined && (
            <>
              <h4 className="font-semibold text-md p-0 m-0 xl:col-span-2 border-0 border-solid border-t mt-4 border-t-divider">
                {t('COPT_BILLING_DATA_CONFIG_TITLE')}
              </h4>
              {(updatedBillingDataConfig?.battery_charge_threshold !== undefined ||
                updatedBillingDataConfig?.grid_connection_power_threshold !== undefined) && (
                <div className="flex gap-2">
                  {updatedBillingDataConfig?.battery_charge_threshold !== undefined && (
                    <NumberInput
                      value={
                        updatedBillingDataConfig?.battery_charge_threshold === null
                          ? null
                          : updatedBillingDataConfig?.battery_charge_threshold * 1000
                      }
                      name="billing_data_config.battery_charge_threshold"
                      label={t('COPT_BILLING_DATA_CONFIG_BATTERY_CHARGE_THRESHOLD')}
                      title={t('COPT_BILLING_DATA_CONFIG_BATTERY_CHARGE_THRESHOLD')}
                      required
                      onChange={(value: number) => {
                        setUpdatedBillingDataConfig({
                          ...updatedBillingDataConfig,
                          battery_charge_threshold: value != null && !Number.isNaN(value) ? value / 1000 : null,
                        });
                      }}
                      disabled={readOnly('battery_charge_threshold')}
                    />
                  )}
                  {updatedBillingDataConfig?.grid_connection_power_threshold !== undefined && (
                    <NumberInput
                      value={
                        updatedBillingDataConfig?.grid_connection_power_threshold === null
                          ? null
                          : updatedBillingDataConfig?.grid_connection_power_threshold * 1000
                      }
                      name="billing_data_config.grid_connection_power_threshold"
                      label={t('COPT_BILLING_DATA_CONFIG_GRID_CONNECTION_POWER_THRESHOLD')}
                      title={t('COPT_BILLING_DATA_CONFIG_GRID_CONNECTION_POWER_THRESHOLD')}
                      required
                      onChange={(value: number) => {
                        setUpdatedBillingDataConfig({
                          ...updatedBillingDataConfig,
                          grid_connection_power_threshold: value != null && !Number.isNaN(value) ? value / 1000 : null,
                        });
                      }}
                      disabled={readOnly('grid_connection_power_threshold')}
                      maxValue={0}
                    />
                  )}
                </div>
              )}
              {(updatedBillingDataConfig?.battery_not_used_volume_threshold !== undefined ||
                updatedBillingDataConfig?.pv_deviation_threshold_relative !== undefined) && (
                <div className="flex gap-2">
                  {updatedBillingDataConfig?.battery_not_used_volume_threshold !== undefined && (
                    <NumberInput
                      value={
                        updatedBillingDataConfig?.battery_not_used_volume_threshold === null
                          ? null
                          : updatedBillingDataConfig?.battery_not_used_volume_threshold * 1000
                      }
                      name="billing_data_config.battery_not_used_volume_threshold"
                      label={t('COPT_BILLING_DATA_CONFIG_BATTERY_NOT_USED_VOLUME_THRESHOLD')}
                      title={t('COPT_BILLING_DATA_CONFIG_BATTERY_NOT_USED_VOLUME_THRESHOLD')}
                      onChange={(value: Nullable<number>) => {
                        setUpdatedBillingDataConfig({
                          ...updatedBillingDataConfig,
                          battery_not_used_volume_threshold:
                            value != null && !Number.isNaN(value) ? value / 1000 : null,
                        });
                      }}
                      disabled={readOnly('battery_not_used_volume_threshold')}
                      notNegative
                    />
                  )}
                  {updatedBillingDataConfig?.pv_deviation_threshold_relative !== undefined && (
                    <NumberInput
                      value={
                        updatedBillingDataConfig?.pv_deviation_threshold_relative === null
                          ? null
                          : Math.round(updatedBillingDataConfig?.pv_deviation_threshold_relative * 100)
                      }
                      name="billing_data_config.pv_deviation_threshold_relative"
                      label={t('COPT_BILLING_DATA_CONFIG_PV_DEVIATION_THRESHOLD_RELATIVE')}
                      title={t('COPT_BILLING_DATA_CONFIG_PV_DEVIATION_THRESHOLD_RELATIVE')}
                      onChange={(value: Nullable<number>) => {
                        setUpdatedBillingDataConfig({
                          ...updatedBillingDataConfig,
                          pv_deviation_threshold_relative:
                            value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null,
                        });
                      }}
                      disabled={readOnly('pv_deviation_threshold_relative')}
                      maxValue={100}
                      rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                    />
                  )}
                </div>
              )}
              {(updatedBillingDataConfig?.pv_deviation_threshold_absolute !== undefined ||
                updatedBillingDataConfig?.grid_feedin_tolerance_absolute !== undefined) && (
                <div className="flex gap-2">
                  {updatedBillingDataConfig?.pv_deviation_threshold_absolute !== undefined && (
                    <NumberInput
                      value={
                        updatedBillingDataConfig?.pv_deviation_threshold_absolute === null
                          ? null
                          : updatedBillingDataConfig?.pv_deviation_threshold_absolute * 1000
                      }
                      name="billing_data_config.pv_deviation_threshold_absolute"
                      label={t('COPT_BILLING_DATA_CONFIG_PV_DEVIATION_THRESHOLD_ABSOLUTE')}
                      title={t('COPT_BILLING_DATA_CONFIG_PV_DEVIATION_THRESHOLD_ABSOLUTE')}
                      onChange={(value: Nullable<number>) => {
                        setUpdatedBillingDataConfig({
                          ...updatedBillingDataConfig,
                          pv_deviation_threshold_absolute: value != null && !Number.isNaN(value) ? value / 1000 : null,
                        });
                      }}
                      disabled={readOnly('pv_deviation_threshold_absolute')}
                      rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                    />
                  )}
                  {updatedBillingDataConfig?.grid_feedin_tolerance_absolute !== undefined && (
                    <NumberInput
                      value={
                        updatedBillingDataConfig?.grid_feedin_tolerance_absolute === null
                          ? null
                          : updatedBillingDataConfig?.grid_feedin_tolerance_absolute * 1000
                      }
                      name="billing_data_config.grid_feedin_tolerance_absolute"
                      label={t('COPT_BILLING_DATA_CONFIG_GRID_FEEDIN_TOLERANCE_ABSOLUTE')}
                      title={t('COPT_BILLING_DATA_CONFIG_GRID_FEEDIN_TOLERANCE_ABSOLUTE')}
                      onChange={(value: Nullable<number>) => {
                        setUpdatedBillingDataConfig({
                          ...updatedBillingDataConfig,
                          grid_feedin_tolerance_absolute: value != null && !Number.isNaN(value) ? value / 1000 : null,
                        });
                      }}
                      disabled={readOnly('grid_feedin_tolerance_absolute')}
                      rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                    />
                  )}
                </div>
              )}
              {updatedBillingDataConfig?.availability_metric_method !== undefined && (
                <FormControl style={{ paddingTop: '0px', marginTop: '1em', width: '100%' }}>
                  <InputLabel id="availability-metric-method">
                    {t('COPT_BILLING_DATA_CONFIG_AVAILABILITY_METRIC_METHOD')}
                  </InputLabel>
                  <Select
                    title={t('COPT_BILLING_DATA_CONFIG_AVAILABILITY_METRIC_METHOD')}
                    id="availability-metric-method"
                    value={updatedBillingDataConfig?.availability_metric_method}
                    disabled={readOnly('availability-metric-method')}
                    label={t('COPT_BILLING_DATA_CONFIG_AVAILABILITY_METRIC_METHOD')}
                    onChange={(e) => {
                      setUpdatedBillingDataConfig({
                        ...updatedBillingDataConfig,
                        availability_metric_method: e.target.value as AvailabilityMetricMethod,
                      });
                    }}
                  >
                    {Object.entries(AvailabilityMetricMethod).map((element) => (
                      <MenuItem value={element[0]} key={element[0]}>
                        {element[1]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {updatedBillingDataConfig?.ppa_billed !== undefined && (
                <label
                  className="flex items-center text-sm font-light mr-4 xl:col-span-2"
                  title={t('COPT_BILLING_DATA_CONFIG_PPA_BILLED')}
                >
                  <input
                    type="checkbox"
                    checked={!!updatedBillingDataConfig?.ppa_billed}
                    disabled={readOnly('ppa_billed')}
                    onChange={() =>
                      setUpdatedBillingDataConfig({
                        ...updatedBillingDataConfig,
                        ppa_billed: !updatedBillingDataConfig?.ppa_billed,
                      })
                    }
                    className="w-4 h-4 mr-2 accent-green-200"
                  />
                  {t('COPT_BILLING_DATA_CONFIG_PPA_BILLED')}
                </label>
              )}
            </>
          )}
          {optimizationSchedule !== undefined && (
            <>
              <h4 className="font-semibold text-md p-0 m-0 xl:col-span-2 border-0 border-solid border-t mt-4 border-t-divider">
                {t('COPT_OPT_RUN_SCHEDULE')}
                <InfoTooltip text={t('COPT_OPT_RUN_SCHEDULE_TOOLTIP')} field="opt_run_schedule" />
              </h4>
              <div>
                <label className="flex items-center text-sm font-light mr-4">
                  <input
                    type="checkbox"
                    checked={optimizationSchedule === null}
                    disabled={readOnly('opt_run_schedule')}
                    onChange={() => {
                      const schedule =
                        optimizationSchedule === null
                          ? {
                              minutes: getMinutes(
                                roundToNearestMinutes(new Date(), { nearestTo: 15, roundingMethod: 'ceil' }),
                              ),
                              hours: getHours(new Date()),
                              daysOfWeek: undefined,
                            }
                          : null;
                      setOptimizationSchedule(schedule);
                      setChanges(
                        changes.set(
                          'opt_run_schedule',
                          schedule !== null ? `${schedule.minutes} ${schedule.hours} * * *` : null,
                        ),
                      );
                    }}
                    className="w-4 h-4 mr-2 accent-green-200"
                  />
                  {t('COPT_OPT_RUN_SCHEDULE_AUTO')}
                </label>

                <div className="my-4">
                  <TimePicker
                    label={t('COPT_OPT_RUN_SCHEDULE_TIME')}
                    value={
                      optimizationSchedule !== null && (optimizationSchedule.minutes || optimizationSchedule?.hours)
                        ? setHours(setMinutes(new Date(), optimizationSchedule.minutes), optimizationSchedule.hours)
                        : new Date()
                    }
                    disabled={readOnly('opt_run_schedule') || optimizationSchedule === null}
                    hoursStep={1}
                    minutesStep={15}
                    onChange={({ value }) => {
                      const minutes = getMinutes(value);
                      const hours = getHours(value);
                      optimizationSchedule !== null &&
                        setOptimizationSchedule({
                          ...optimizationSchedule,
                          minutes,
                          hours,
                        });
                      setChanges(
                        changes.set('opt_run_schedule', `${minutes} ${hours} * * ${optimizationSchedule?.daysOfWeek}`),
                      );
                    }}
                  />
                </div>
                <ValidatedWeekdaySelector
                  name="weekSelector"
                  selected={
                    optimizationSchedule?.daysOfWeek !== undefined
                      ? ([...optimizationSchedule.daysOfWeek].reduce(
                          (a, v) => ({ ...a, [v]: true }),
                          {},
                        ) as WeekdaySelection)
                      : undefined
                  }
                  disabled={readOnly('opt_run_schedule') || optimizationSchedule === null}
                  className={clsx(
                    'p-4 rounded-md bg-white border-solid border border-divider',

                    {
                      'opacity-70': optimizationSchedule === null,
                    },
                  )}
                  onChange={(selected) => {
                    const daysOfWeek = Object.entries(selected)
                      .filter(([, isSelected]) => isSelected)
                      .map(([day]) => day);
                    if (optimizationSchedule !== null) {
                      setOptimizationSchedule({
                        ...optimizationSchedule,
                        daysOfWeek,
                      });
                      setChanges(
                        changes.set(
                          'opt_run_schedule',
                          `${optimizationSchedule.minutes} ${optimizationSchedule.hours} * * ${daysOfWeek}`,
                        ),
                      );
                    }
                  }}
                  rules={optimizationSchedule !== null ? weekdaySelectionRules : undefined}
                />
              </div>
            </>
          )}
        </ValidatedContainer>
      </DrawerContent>

      <DrawerFooter testId="forecast-assistant-footer">
        <DrawerButtons
          onCancel={() => {
            resetValues();
            onClose();
          }}
          onSave={updateValues}
          isLoading={isSaving}
          disableSave={isSaving || readOnly()}
          cancelLabel={t('SHARED_CANCEL')}
          saveLabel={t('SHARED_SUBMIT')}
        />
      </DrawerFooter>
    </Drawer>
  );
}

export default OptimizationParametersSidePanel;
